import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DAYS, dayLabel } from "@/lib/format";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function DeliverySettings() {
  const [s, setS] = useState(null);
  const [newDate, setNewDate] = useState("");

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    await api.put("/admin/settings", s);
    toast.success("Delivery settings saved");
  };
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setZone = (i, k, v) => setS((p) => { const z = [...p.delivery_zones]; z[i] = { ...z[i], [k]: v }; return { ...p, delivery_zones: z }; });
  const addZone = () => set("delivery_zones", [...s.delivery_zones, { id: `zone-${Date.now()}`, name: "New Zone", zips: [], fee: 5, min_order: 15, days: [] }]);
  const removeZone = (i) => set("delivery_zones", s.delivery_zones.filter((_, x) => x !== i));
  const setSchedule = (day, patch) => set("delivery_schedule", { ...s.delivery_schedule, [day]: { ...s.delivery_schedule[day], ...patch } });
  const setWindow = (day, wi, k, v) => { const w = [...(s.delivery_schedule[day].windows || [])]; w[wi] = { ...w[wi], [k]: v }; setSchedule(day, { windows: w }); };
  const addWindow = (day) => setSchedule(day, { windows: [...(s.delivery_schedule[day].windows || []), { start: "17:00", end: "20:00" }] });
  const removeWindow = (day, wi) => setSchedule(day, { windows: s.delivery_schedule[day].windows.filter((_, x) => x !== wi) });

  if (!s) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={36} /></div></AdminLayout>;

  const field = "bg-input border-gold/25 text-cream rounded-xl";
  const card = "gold-border-card rounded-xl bg-card-surface p-6";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl text-cream">Delivery Settings</h1>
        <Button className="btn-gold rounded-full font-semibold" onClick={save} data-testid="save-delivery-btn"><Save size={16} className="mr-1" /> Save All</Button>
      </div>

      <div className="space-y-6">
        {/* Toggles */}
        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Fulfillment Methods</h2>
          {[["local_delivery_enabled", "Local Delivery"]].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between py-2">
              <span className="text-cream/90">{l}</span>
              <Switch checked={!!s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
            </div>
          ))}
        </div>

        {/* Prep + limits */}
        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Order Rules</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-cream/70 text-sm">Minimum Preparation Time (hours)</Label><Input type="number" className={field} value={s.min_prep_hours} onChange={(e) => set("min_prep_hours", Number(e.target.value))} data-testid="min-prep-hours" /></div>
            <div><Label className="text-cream/70 text-sm">Max Orders Per Time Slot</Label><Input type="number" className={field} value={s.max_orders_per_slot} onChange={(e) => set("max_orders_per_slot", Number(e.target.value))} data-testid="max-orders-slot" /></div>
          </div>
        </div>

        {/* Delivery Zones */}
        <div className={card}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl text-gold">Delivery Zones</h2>
            <Button size="sm" variant="outline" className="bg-transparent border-gold/40 text-gold rounded-lg" onClick={addZone} data-testid="add-zone-btn"><Plus size={14} className="mr-1" /> Add Zone</Button>
          </div>
          <div className="space-y-4">
            {s.delivery_zones.map((z, i) => (
              <div key={z.id} className="border border-gold/20 rounded-xl p-4 space-y-3" data-testid={`zone-${i}`}>
                <div className="flex justify-between items-center">
                  <Input className={`${field} max-w-xs`} value={z.name} onChange={(e) => setZone(i, "name", e.target.value)} data-testid={`zone-name-${i}`} />
                  <button onClick={() => removeZone(i)} className="text-cream/40 hover:text-destructive"><Trash2 size={16} /></button>
                </div>
                <div><Label className="text-cream/60 text-xs">ZIP Codes (comma separated)</Label><Input className={field} value={(z.zips || []).join(", ")} onChange={(e) => setZone(i, "zips", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`zone-zips-${i}`} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-cream/60 text-xs">Delivery Fee ($)</Label><Input type="number" className={field} value={z.fee} onChange={(e) => setZone(i, "fee", Number(e.target.value))} data-testid={`zone-fee-${i}`} /></div>
                  <div><Label className="text-cream/60 text-xs">Minimum Order ($)</Label><Input type="number" className={field} value={z.min_order} onChange={(e) => setZone(i, "min_order", Number(e.target.value))} data-testid={`zone-min-${i}`} /></div>
                </div>
                <div>
                  <Label className="text-cream/60 text-xs">Available Days</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {DAYS.map((d) => (
                      <button key={d} onClick={() => setZone(i, "days", (z.days || []).includes(d) ? z.days.filter((x) => x !== d) : [...(z.days || []), d])}
                        className={`text-xs px-2.5 py-1 rounded-full border ${(z.days || []).includes(d) ? "border-gold bg-gold/15 text-gold" : "border-gold/20 text-cream/50"}`} data-testid={`zone-${i}-day-${d}`}>{dayLabel(d).slice(0, 3)}</button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly schedule */}
        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Delivery Availability (Weekly)</h2>
          <div className="space-y-3">
            {DAYS.map((day) => {
              const cfg = s.delivery_schedule[day] || { available: false, windows: [] };
              return (
                <div key={day} className="border border-gold/15 rounded-xl p-3" data-testid={`schedule-${day}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-cream font-medium w-28">{dayLabel(day)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-cream/50 text-xs">{cfg.available ? "Available" : "Unavailable"}</span>
                      <Switch checked={cfg.available} onCheckedChange={(v) => setSchedule(day, { available: v })} data-testid={`schedule-toggle-${day}`} />
                    </div>
                  </div>
                  {cfg.available && (
                    <div className="mt-3 space-y-2">
                      {(cfg.windows || []).map((w, wi) => (
                        <div key={wi} className="flex items-center gap-2">
                          <Input type="time" className={`${field} w-32`} value={w.start} onChange={(e) => setWindow(day, wi, "start", e.target.value)} data-testid={`window-start-${day}-${wi}`} />
                          <span className="text-cream/50">to</span>
                          <Input type="time" className={`${field} w-32`} value={w.end} onChange={(e) => setWindow(day, wi, "end", e.target.value)} data-testid={`window-end-${day}-${wi}`} />
                          <button onClick={() => removeWindow(day, wi)} className="text-cream/40 hover:text-destructive"><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" className="bg-transparent border-gold/40 text-gold rounded-lg text-xs" onClick={() => addWindow(day)} data-testid={`add-window-${day}`}><Plus size={12} className="mr-1" /> Add Time Window</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Blocked dates */}
        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Blocked Dates & Holidays</h2>
          <div className="flex gap-2 mb-3">
            <Input type="date" className={`${field} max-w-xs`} value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="blocked-date-input" />
            <Button variant="outline" className="bg-transparent border-gold/40 text-gold rounded-lg" onClick={() => { if (newDate && !s.blocked_dates.includes(newDate)) { set("blocked_dates", [...s.blocked_dates, newDate]); setNewDate(""); } }} data-testid="add-blocked-date"><Plus size={14} /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {s.blocked_dates.map((d) => (
              <span key={d} className="flex items-center gap-1 bg-destructive/15 text-destructive text-sm px-3 py-1 rounded-full" data-testid={`blocked-${d}`}>
                {d} <button onClick={() => set("blocked_dates", s.blocked_dates.filter((x) => x !== d))}><Trash2 size={12} /></button>
              </span>
            ))}
            {s.blocked_dates.length === 0 && <p className="text-cream/40 text-sm">No blocked dates.</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
